/*
|--------------------------------------------------------------------------
| Zero1Hub Product Card
|--------------------------------------------------------------------------
*/

class Z1HProductCard {

    constructor(card) {

        this.card = card;

        this.loadData();

        this.cacheDOM();

        this.detectOptions();

        this.buildVariantIndex();

        this.init();

    }

    /*
    |--------------------------------------------------------------------------
    | Load Product JSON
    |--------------------------------------------------------------------------
    */

    loadData() {

        const json =
            this.card.querySelector(".z1h-product-data");

        if (!json) {

            throw new Error(
                "Missing .z1h-product-data"
            );

        }

        const data =
            JSON.parse(json.textContent);

        this.variants =
            data.variants || [];

        this.options =
            data.options || [];

    }

    /*
    |--------------------------------------------------------------------------
    | Cache DOM
    |--------------------------------------------------------------------------
    */

    cacheDOM() {

        this.gallery =
            this.card.querySelector(".z1h-gallery__track");

        this.slides =
            [...this.card.querySelectorAll(".z1h-gallery__slide")];

        this.prev =
            this.card.querySelector(".z1h-gallery__prev");

        this.next =
            this.card.querySelector(".z1h-gallery__next");

        this.dots =
            this.card.querySelector(".z1h-gallery__dots");

        this.price =
            this.card.querySelector(".z1h-product__price");

        this.atc =
            this.card.querySelector(".z1h-product__atc");

        this.swatches =
            [...this.card.querySelectorAll(".z1h-product__swatch")];

        this.sizes =
            [...this.card.querySelectorAll(".z1h-product__size")];

    }

    /*
    |--------------------------------------------------------------------------
    | Detect Option Positions
    |--------------------------------------------------------------------------
    */

    detectOptions() {

        this.colorIndex = -1;

        this.sizeIndex = -1;

        this.options.forEach((option, index) => {

            option = option.toLowerCase();

            if (
                option.includes("color") ||
                option.includes("colour")
            ) {

                this.colorIndex = index;

            }

            if (
                option.includes("size")
            ) {

                this.sizeIndex = index;

            }

        });

    }

    /*
    |--------------------------------------------------------------------------
    | Build Variant Lookup
    |--------------------------------------------------------------------------
    */

    buildVariantIndex() {

        this.variantIndex = new Map();

        this.variants.forEach(variant => {

            const key =
                variant.options
                    .map(v =>
                        String(v)
                            .trim()
                            .toLowerCase()
                    )
                    .join("|");

            this.variantIndex.set(
                key,
                variant
            );

        });

    }

    /*
    |--------------------------------------------------------------------------
    | Init
    |--------------------------------------------------------------------------
    */

    init() {

        this.currentVariant =

            this.findVariant(

                this.card.dataset.currentVariant

            )

            ||

            this.variants[0];

        this.selectedColor =

            this.colorIndex > -1

                ?

                this.currentVariant.options[
                    this.colorIndex
                ]

                :

                null;

        this.selectedSize =

            this.sizeIndex > -1

                ?

                this.currentVariant.options[
                    this.sizeIndex
                ]

                :

                null;

        this.currentSlide = 0;

        this.visibleSlides =
            [...this.slides];

        this.initGallery();

        this.renderInitialState();

        this.bindEvents();

    }

    /*
    |--------------------------------------------------------------------------
    | Initial UI
    |--------------------------------------------------------------------------
    */

    renderInitialState() {

        this.highlightSelections();

        this.buildDots();

        this.updatePrice();

        this.updateButton();

        this.updateGallery();
        this.highlightSelections();
        this.updateColourHeading();

    }

    /*
    |--------------------------------------------------------------------------
    | Find Variant
    |--------------------------------------------------------------------------
    */

    findVariant(id) {

        return this.variants.find(

            variant =>

                String(variant.id)

                ===

                String(id)

        );

    }

    /*
    |--------------------------------------------------------------------------
    | Highlight Current Variant
    |--------------------------------------------------------------------------
    */

    highlightSelections() {

        this.swatches.forEach(button => {

            button.classList.toggle(

                "is-active",

                button.dataset.color.toLowerCase()

                ===

                String(this.selectedColor)
                    .toLowerCase()

            );

        });

        this.sizes.forEach(button => {

            button.classList.toggle(

                "is-active",

                button.dataset.size

                ===

                this.selectedSize

            );

        });

    }

    /*
    |--------------------------------------------------------------------------
    | Events
    |--------------------------------------------------------------------------
    */

    bindEvents() {

        this.prev?.addEventListener(

            "click",

            () => this.previousSlide()

        );

        this.next?.addEventListener(

            "click",

            () => this.nextSlide()

        );

        this.swatches.forEach(button => {

            button.addEventListener(

                "click",

                () => this.selectColor(button)

            );

        });

        this.sizes.forEach(button => {

            button.addEventListener(

                "click",

                () => this.selectSize(button)

            );

        });

        this.atc?.addEventListener(

            "click",

            () => this.addToCart()

        );

    }

    /*
    |--------------------------------------------------------------------------
    | Placeholder Methods
    |--------------------------------------------------------------------------
    */

    selectColor(){}

    selectSize(){}

    updateVariant(){}

    updateGallery(){}

    previousSlide(){}

    nextSlide(){}

    goToSlide(){}

    updatePrice(){}

    updateButton(){}

    buildDots(){}

    addToCart(){}

    formatMoney(){}

        /*
    |--------------------------------------------------------------------------
    | PART 2 - Variant Engine
    |--------------------------------------------------------------------------
    */

    selectColor(button) {

        this.selectedColor = button.dataset.color.trim();

        this.highlightSelections();

        this.updateAvailableSizes();

        this.autoSelectSize();

        this.updateVariant();
        this.highlightSelections();
        this.updateColourHeading();

    }

    selectSize(button) {

        if (button.disabled)
            return;

        this.selectedSize = button.dataset.size.trim();

        this.highlightSelections();
        this.updateColourHeading();

        this.updateVariant();

    }

    updateAvailableSizes() {

        if (this.sizeIndex === -1)
            return;

        this.sizes.forEach(button => {

            const size =
                button.dataset.size
                    .trim()
                    .toLowerCase();

            const available = this.variants.some(variant => {

                const color =
                    String(
                        variant.options[this.colorIndex]
                    )
                    .trim()
                    .toLowerCase();

                const variantSize =
                    String(
                        variant.options[this.sizeIndex]
                    )
                    .trim()
                    .toLowerCase();

                return (

                    color ===
                    this.selectedColor.toLowerCase()

                    &&

                    variantSize === size

                    &&

                    variant.available

                );

            });

            button.disabled = !available;

            button.classList.toggle(

                "is-disabled",

                !available

            );

        });

    }

    autoSelectSize() {

        if (this.sizeIndex === -1)
            return;

        const active =

            this.sizes.find(

                button =>

                    !button.disabled

            );

        if (!active)
            return;

        this.selectedSize =
            active.dataset.size;

        this.highlightSelections();

    }

    updateVariant() {

        let key = [];

        if (this.colorIndex > -1) {

            key[this.colorIndex] =

                this.selectedColor
                    .trim()
                    .toLowerCase();

        }

        if (this.sizeIndex > -1) {

            key[this.sizeIndex] =

                this.selectedSize
                    .trim()
                    .toLowerCase();

        }

        /*
        ----------------------------------
        Build Complete Key
        ----------------------------------
        */

        this.options.forEach((option, index) => {

            if (key[index])
                return;

            key[index] =

                this.currentVariant.options[index]
                    .trim()
                    .toLowerCase();

        });

        const variant =

            this.variantIndex.get(

                key.join("|")

            );

        if (!variant)
            return;

        this.currentVariant = variant;

        this.card.dataset.currentVariant =
            variant.id;

        this.highlightSelections();

        this.updatePrice();

        this.updateButton();

        this.updateGallery();

    }

    updatePrice() {

        if (!this.price)
            return;

        let html =

            `<span class="z1h-product__current-price">
                ${this.formatMoney(
                    this.currentVariant.price
                )}
            </span>`;

        if (

            this.currentVariant.compare_at_price >

            this.currentVariant.price

        ) {

            const compare =
                this.currentVariant.compare_at_price;

            const price =
                this.currentVariant.price;

            const discount = Math.round(

                ((compare - price)

                /

                compare)

                *

                100

            );

            html +=

            `<span class="z1h-product__compare-price">

                ${this.formatMoney(compare)}

            </span>

            <span class="z1h-product__discount">

                ${discount}% OFF

            </span>`;

        }

        this.price.innerHTML = html;

    }

    updateButton() {

        if (!this.atc)
            return;

        this.atc.dataset.variantId =
            this.currentVariant.id;

        if (

            this.currentVariant.available

        ) {

            this.atc.disabled = false;

            this.atc.textContent =
                "Add to Cart";

        }

        else {

            this.atc.disabled = true;

            this.atc.textContent =
                "Sold Out";

        }

    }

        /*
    |--------------------------------------------------------------------------
    | PART 3 - Gallery Engine
    |--------------------------------------------------------------------------
    */

    updateGallery() {

        if (!this.gallery)
            return;

        const color =

            this.selectedColor
                ? this.selectedColor.toLowerCase()
                : null;

        this.visibleSlides = [];

        this.slides.forEach(slide => {

            const alt =
                String(
                    slide.dataset.alt || ""
                ).toLowerCase();

            let show = true;

            if (color) {

                show = alt.includes(color);

            }

            slide.style.display =
                show
                    ? ""
                    : "none";

            if (show)
                this.visibleSlides.push(slide);

        });

        /*
        ------------------------------------
        No matching images?
        Show all images.
        ------------------------------------
        */

        if (!this.visibleSlides.length) {

            this.slides.forEach(slide => {

                slide.style.display = "";

            });

            this.visibleSlides =
                [...this.slides];

        }

        this.buildDots();

        this.goToVariantImage();

    }

    goToVariantImage() {

        let index = 0;

        if (

            this.currentVariant.featured_media

        ) {

            const mediaId =

                String(

                    this.currentVariant
                        .featured_media.id

                );

            const found =

                this.visibleSlides.findIndex(

                    slide =>

                        String(

                            slide.dataset.mediaId

                        ) === mediaId

                );

            if (found > -1)

                index = found;

        }

        this.goToSlide(

            index,

            false

        );

    }

    previousSlide() {

        this.goToSlide(

            this.currentSlide - 1

        );

    }

    nextSlide() {

        this.goToSlide(

            this.currentSlide + 1

        );

    }

    goToSlide(

        index,

        smooth = true

    ) {

        if (!this.visibleSlides.length)
            return;

        if (index < 0)

            index =
                this.visibleSlides.length - 1;

        if (

            index >=

            this.visibleSlides.length

        )

            index = 0;

        this.currentSlide = index;

        const slide =

            this.visibleSlides[index];

        this.gallery.scrollTo({

            left: slide.offsetLeft,

            behavior:

                smooth

                    ? "smooth"

                    : "auto"

        });

        this.updateDots();

    }

    buildDots() {

        if (!this.dots)
            return;

        this.dots.innerHTML = "";

        this.visibleSlides.forEach(

            (_, index) => {

                const dot =

                    document.createElement(

                        "button"

                    );

                dot.type = "button";

                dot.className =
                    "z1h-gallery__dot";

                dot.dataset.index =
                    index;

                dot.addEventListener(

                    "click",

                    () =>

                        this.goToSlide(index)

                );

                this.dots.append(dot);

            }

        );

        this.updateDots();

    }

    updateDots() {

        if (!this.dots)
            return;

        this.dots
            .querySelectorAll(

                ".z1h-gallery__dot"

            )

            .forEach((dot, index) => {

                dot.classList.toggle(

                    "is-active",

                    index ===

                    this.currentSlide

                );

            });

    }

    initSwipe() {

        if (!this.gallery)
            return;

        let startX = 0;

        let endX = 0;

        this.gallery.addEventListener(

            "touchstart",

            event => {

                startX =

                    event.touches[0]
                        .clientX;

            },

            {

                passive: true

            }

        );

        this.gallery.addEventListener(

            "touchmove",

            event => {

                endX =

                    event.touches[0]
                        .clientX;

            },

            {

                passive: true

            }

        );

        this.gallery.addEventListener(

            "touchend",

            () => {

                const distance =

                    startX - endX;

                if (

                    Math.abs(distance)

                    <

                    40

                )

                    return;

                if (

                    distance > 0

                )

                    this.nextSlide();

                else

                    this.previousSlide();

            }

        );

    }

    observeGallery() {

        if (!this.gallery)
            return;

        this.gallery.addEventListener(

            "scroll",

            this.debounce(() => {

                let closest = 0;

                let distance = Infinity;

                this.visibleSlides.forEach(

                    (slide, index) => {

                        const d =

                            Math.abs(

                                slide.offsetLeft -

                                this.gallery.scrollLeft

                            );

                        if (

                            d < distance

                        ) {

                            distance = d;

                            closest = index;

                        }

                    }

                );

                this.currentSlide =
                    closest;

                this.updateDots();

            }, 50)

        );

    }

    initGallery() {

        this.visibleSlides =
            [...this.slides];

        this.buildDots();

        this.initSwipe();

        this.observeGallery();

        this.goToSlide(

            0,

            false

        );

    }

    updateColourHeading() {

    const trendingName = this.card.querySelector(".z1h-trending_colour_name");
    const classicName = this.card.querySelector(".z1h-classic_colour_name");

    if (trendingName) trendingName.textContent = "";
    if (classicName) classicName.textContent = "";

    const active = this.card.querySelector(".z1h-product__swatch.is-active");

    if (!active) return;

    const colour =
        active.dataset.color
            .replace(/\b\w/g, c => c.toUpperCase());

    if (active.dataset.group === "trending") {

        if (trendingName)
            trendingName.textContent = colour;

    } else {

        if (classicName)
            classicName.textContent = colour;

    }

}

        /*
    |--------------------------------------------------------------------------
    | PART 4 - Cart & Helpers
    |--------------------------------------------------------------------------
    */

async addToCart() {

    if (!this.currentVariant || !this.currentVariant.available) return;

    this.atc.disabled = true;
    this.atc.textContent = "Adding...";

    try {

        // Check if cart is empty BEFORE adding
        const cartResponse = await fetch("/cart.js");
        const cart = await cartResponse.json();

        const wasEmpty = cart.item_count === 0;

        // Add product
        const response = await fetch("/cart/add.js", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json"
            },
            body: JSON.stringify({
                id: this.currentVariant.id,
                quantity: 1
            })
        });

        if (!response.ok) {
            throw await response.json();
        }

        // If this was the first item, simply refresh the page
        if (wasEmpty) {
            window.location.reload();
            return;
        }

        // Otherwise refresh the cart drawer HTML silently
        const drawerResponse = await fetch("/cart?section_id=cart-drawer");
        const drawerHTML = await drawerResponse.text();

        const parser = new DOMParser();
        const drawerDocument = parser.parseFromString(drawerHTML, "text/html");

        const newDrawer = drawerDocument.querySelector("cart-drawer");
        const currentDrawer = document.querySelector("cart-drawer");

        if (newDrawer && currentDrawer) {
            currentDrawer.innerHTML = newDrawer.innerHTML;
        }

        this.atc.textContent = "Added ✓";

        setTimeout(() => {
            this.updateButton();
        }, 1200);

    } catch (error) {

        console.error(error);

        this.atc.textContent = error.description || "Error";

        setTimeout(() => {
            this.updateButton();
        }, 1500);

    } finally {

        this.atc.disabled = !this.currentVariant.available;

    }

}

    /*
    |--------------------------------------------------------------------------
    | Money Formatter
    |--------------------------------------------------------------------------
    */

    formatMoney(cents) {

        cents = Number(cents);

        if (

            window.Shopify &&

            Shopify.formatMoney

        ) {

            return Shopify.formatMoney(

                cents,

                window.theme?.moneyFormat ||

                "${{amount}}"

            );

        }

        return new Intl.NumberFormat(

            document.documentElement.lang,

            {

                style:"currency",

                currency:

                    window.Shopify?.currency?.active ||

                    "USD"

            }

        ).format(

            cents/100

        );

    }

    /*
    |--------------------------------------------------------------------------
    | Debounce
    |--------------------------------------------------------------------------
    */

    debounce(callback, wait=100){

        let timeout;

        return (...args)=>{

            clearTimeout(timeout);

            timeout=setTimeout(()=>{

                callback.apply(this,args);

            },wait);

        }

    }

    /*
    |--------------------------------------------------------------------------
    | Throttle
    |--------------------------------------------------------------------------
    */

    throttle(callback,limit=100){

        let waiting=false;

        return (...args)=>{

            if(waiting) return;

            callback.apply(this,args);

            waiting=true;

            setTimeout(()=>{

                waiting=false;

            },limit);

        }

    }

} // END OF CLASS



/*
|--------------------------------------------------------------------------
| Initialize Product Cards
|--------------------------------------------------------------------------
*/

document.addEventListener(

    "DOMContentLoaded",

    ()=>{

        document

            .querySelectorAll(".z1h-product")

            .forEach(card=>{

                new Z1HProductCard(card);

            });

    }

);

/*
|--------------------------------------------------------------------------
| Collection Filter
|--------------------------------------------------------------------------
*/

document.addEventListener("DOMContentLoaded", () => {

    document.querySelectorAll(".z1h-collection-variants").forEach(section => {

        const chips = section.querySelectorAll(
            ".z1h-collection-variants__collection-chip"
        );

        const products = section.querySelectorAll(
            ".z1h_collection_product_wrapper"
        );

        chips.forEach(chip => {

            chip.addEventListener("click", () => {

                chips.forEach(c => c.classList.remove("active"));
                chip.classList.add("active");

                const selected = chip.dataset.collectionId;

                products.forEach(product => {

                    const show =
                        selected === "all" ||
                        product.dataset.collectionId === selected;

                    product.style.display = show ? "" : "none";

                });

            });

        });

    });

});

document.addEventListener('DOMContentLoaded', () => {

  document.querySelectorAll('.size-chart-block').forEach(block => {

    const header = block.querySelector('.size-chart-header');
    const body = block.querySelector('.size-chart-body');

    if (!header || !body) return;

    // Create modal
    const modal = document.createElement('div');
    modal.className = 'z1h-size-chart-modal';

    modal.innerHTML = `
      <div class="z1h-size-chart-overlay"></div>

      <div class="z1h-size-chart-dialog">

        <div class="z1h-size-chart-header-bar">
          <h3>Size Chart</h3>
          <button type="button" class="z1h-size-chart-close">
            &times;
          </button>
        </div>

        <div class="z1h-size-chart-modal-content">
          ${body.innerHTML}
        </div>

      </div>
    `;

    document.body.appendChild(modal);

    // Remove accordion body from product card
    body.remove();

    // Open modal
    header.addEventListener('click', function(e){
      e.preventDefault();
      e.stopPropagation();
      modal.classList.add('active');
      document.body.classList.add('overflow-hidden');
    });

    // Close
    modal.querySelector('.z1h-size-chart-close').addEventListener('click', closeModal);
    modal.querySelector('.z1h-size-chart-overlay').addEventListener('click', closeModal);

    function closeModal(){
      modal.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
    }

  });

});


document.addEventListener("DOMContentLoaded", () => {

    document.querySelectorAll(".z1h-product").forEach(card => {

        const baseUrl = card.dataset.productUrl || card.querySelector(".z1h-product__view-product")?.href.split("?")[0];

        if (!baseUrl) return;

        function updateLinks() {

            const variantId = card.dataset.currentVariant;

            if (!variantId) return;

            const url = `${baseUrl}?variant=${variantId}`;

            // Update View Product button
            card.querySelectorAll(".z1h-product__view-product").forEach(link => {
                link.href = url;
            });

            // Update all gallery image links
            card.querySelectorAll(".z1h-gallery__slide a").forEach(link => {
                link.href = url;
            });

        }

        // Initial update
        updateLinks();

        // Watch for variant changes
        const observer = new MutationObserver(updateLinks);

        observer.observe(card, {
            attributes: true,
            attributeFilter: ["data-current-variant"]
        });

    });

});

/*
|--------------------------------------------------------------------------
| Product Filter
|--------------------------------------------------------------------------
*/

document.addEventListener("DOMContentLoaded", () => {



    const chips = document.querySelectorAll(".z1h-collection-products-chip");
    const cards = document.querySelectorAll(".z1h_collection_product_wrapper");

    // No filter rendered
    if (!chips.length) return;


    chips.forEach(chip => {

        chip.addEventListener("click", () => {

            chips.forEach(c => c.classList.remove("active"));
            chip.classList.add("active");

            const selected = chip.dataset.productId;

            cards.forEach(card => {

                if (
                    !selected ||
                    selected === "all" ||
                    card.dataset.collectionId === selected
                ) {
                    card.style.display = "";
                } else {
                    card.style.display = "none";
                }

            });

        });

    });

});



